import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { FiDownload } from 'react-icons/fi';
import { getAllPartners } from '../../services/api';
import { exportSubcoveragesTripByCompanyName } from '../../utils/tripsSubcoveragesExport';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import PartnerCompanyAutocomplete from './PartnerCompanyAutocomplete';

const ExportSubcoveragesTripModal = ({ show, onHide, onSuccess }) => {
    const { t } = useTranslation();
    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [partnersError, setPartnersError] = useState(null);
    const [companyName, setCompanyName] = useState('');
    const [exportLoading, setExportLoading] = useState(false);
    const [exportError, setExportError] = useState(null);

    const partnerCompanyOptions = useMemo(() => {
        const names = partners
            .map((partner) => partner.company_name?.trim())
            .filter((name) => name);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [partners]);

    const resetState = () => {
        setCompanyName('');
        setExportError(null);
        setExportLoading(false);
    };

    const handleClose = () => {
        resetState();
        onHide();
    };

    const fetchPartners = async () => {
        try {
            setPartnersLoading(true);
            setPartnersError(null);
            const response = await getAllPartners();
            let partnersList = [];
            if (response?.users && Array.isArray(response.users)) {
                partnersList = response.users;
            } else if (Array.isArray(response)) {
                partnersList = response;
            } else if (response?.partners && Array.isArray(response.partners)) {
                partnersList = response.partners;
            } else if (response?.data && Array.isArray(response.data)) {
                partnersList = response.data;
            }
            setPartners(partnersList);
        } catch (error) {
            console.error('Erreur lors du chargement des partenaires:', error);
            setPartners([]);
            setPartnersError(handleApiError(error, t('pages.adminTripsSubcoverages.export.partnersLoadError')));
        } finally {
            setPartnersLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            resetState();
            fetchPartners();
        }
    }, [show]);

    const handleExport = async () => {
        const selectedCompany = companyName.trim();
        if (!selectedCompany) {
            setExportError(t('pages.adminTripsSubcoverages.export.companyRequired'));
            return;
        }

        setExportLoading(true);
        setExportError(null);

        try {
            await exportSubcoveragesTripByCompanyName(selectedCompany);
            onSuccess?.(t('pages.adminTripsSubcoverages.export.success'));
            handleClose();
        } catch (error) {
            console.error('Export subcoverages trip failed:', error);
            setExportError(handleApiError(error, t('pages.adminTripsSubcoverages.export.error')));
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered contentClassName="export-subcoverages-trip-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FiDownload className="me-2" />
                    {t('pages.adminTripsSubcoverages.export.modalTitle')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {exportError && (
                    <Alert variant="danger" className="mb-3" onClose={() => setExportError(null)} dismissible>
                        {exportError}
                    </Alert>
                )}
                {partnersError && (
                    <Alert variant="warning" className="mb-3">
                        {partnersError}
                    </Alert>
                )}
                <Form>
                    <Form.Group>
                        <Form.Label>{t('pages.adminTripsSubcoverages.export.partnerLabel')}</Form.Label>
                        <PartnerCompanyAutocomplete
                            value={companyName}
                            onChange={setCompanyName}
                            options={partnerCompanyOptions}
                            loading={partnersLoading}
                            error={partnersError}
                            disabled={exportLoading}
                            placeholder={t('pages.adminTripsSubcoverages.export.partnerPlaceholder')}
                        />
                        <Form.Text className="text-muted">
                            {t('pages.adminTripsSubcoverages.export.partnerHint')}
                        </Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={exportLoading}>
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="success"
                    onClick={handleExport}
                    disabled={exportLoading || partnersLoading || !companyName.trim()}
                >
                    {exportLoading ? t('pages.adminTripsSubcoverages.export.loading') : t('pages.adminTripsSubcoverages.export.button')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ExportSubcoveragesTripModal;
